import React, { useCallback, useMemo, useState } from 'react';

import { isNil, xor } from 'lodash';
import { MdOutlineOfflinePin } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import type { UnitPendingLeadershipPosition } from '@appTypes/esb';
import { youthItemsSel } from '@modules/advancement/common';
import { userSubUnitMembersSel } from '@modules/advancement/packRoster';
import {
  searchFilterSel,
  selectedRowKeysSel,
  setSelectedRowKeys,
} from '@modules/advancement/pendingItems/duck';
import { isYouthInUserSubunitChecker } from '@modules/advancement/pendingItems/utils';
import {
  achieveFirstClassAndAbove,
  getProgramNameFromUnitLong,
  isArrayGroupBySubUnit,
  searchOnItems,
} from '@modules/advancement/purchaseOrder/utils';
import PurchaseOrdersTableCustomRow from '@modules/advancement/unitAdvancement/components/PurchaseOrdersTable/PurchaseOrdersTableCustomRow';
import { PurchaseOrderTableItem } from '@modules/advancement/unitAdvancement/types';
import { useUnitPendingLeadershipPositions } from '@modules/leadershipPositions/hooks';
import {
  programToProgramTypeName,
  programTypeName,
} from '@modules/shared/constants';
import {
  hasPermissionSel,
  offlineSel,
  packRosterLoadingSel,
  selectedOrganizationSel,
  shouldFilterUsersBySubunitsSel,
} from '@modules/shared/duck/selectors';
import { permissions } from '@modules/shared/permissions';
import { getPreferredName } from '@modules/shared/utils/preferredName';
import { isMobileSel } from '@screen';
import {
  Button,
  Fab,
  PaginationTotal,
  Table,
  TableMobile,
} from '@shared/components';

import {
  LeadershipApproveModalContainer,
  SelectedPositionItem,
} from '../ApproveModal/ApproveModal';
import styles from './PendingItemsTable.less';
import rows from './PendingLeadershipPositionsRows';

interface PendingLeadershipPositionsTable {
  searchQuery?: string;
}

export const PendingLeadershipPositionsTable: React.FC<
  PendingLeadershipPositionsTable
> = ({ searchQuery = '' }) => {
  const dispatch = useDispatch();
  const [modalVisible, setModalVisible] = useState(false);

  const isOffline = useSelector(offlineSel);
  const isMobile = useSelector(isMobileSel);
  const selectedRowKeys: number[] = useSelector(selectedRowKeysSel);
  const currentOrg = useSelector(selectedOrganizationSel);
  const rosterItems = useSelector(youthItemsSel);
  const loadingRoster = useSelector(packRosterLoadingSel);
  const searchTerm: string = useSelector(searchFilterSel);
  const canApprove = useSelector(state =>
    hasPermissionSel(state, permissions.APPROVE_ADVANCEMENTS),
  );
  const shouldFilterUsersBySubunits = useSelector(
    shouldFilterUsersBySubunitsSel,
  );
  const userSubUnitMembers = useSelector(userSubUnitMembersSel);

  const {
    youthPositions: positions,
    response: { isLoading },
  } = useUnitPendingLeadershipPositions();

  const userSubUnitUsersMIDs: number[] = useMemo(
    () =>
      userSubUnitMembers
        .map(member => member.memberId)
        .filter(memberId => !isNil(memberId)) as number[],
    [userSubUnitMembers],
  );

  const isInSubUnit = useCallback(
    (record: UnitPendingLeadershipPosition) =>
      isYouthInUserSubunitChecker(
        shouldFilterUsersBySubunits,
        userSubUnitUsersMIDs,
        record.bsaMemberId,
      ),
    [shouldFilterUsersBySubunits, userSubUnitUsersMIDs],
  );

  const pendingPositions = useMemo(() => {
    if (!searchTerm) return positions || [];

    return (
      positions?.filter(position => {
        const scoutName = position.personFullName || '';
        const searchValue =
          `${position.bsaMemberId} ${scoutName}`.toLowerCase();

        const searchMatch = searchValue.includes(searchTerm.toLowerCase());
        return searchMatch;
      }) || []
    );
  }, [positions, searchTerm]);

  const selectedPositions = useMemo(
    () =>
      positions
        ?.filter(({ userPositionId }) =>
          selectedRowKeys.includes(userPositionId),
        )
        .map(
          item =>
            ({
              position: item,
            } as SelectedPositionItem),
        ) || [],
    [positions, selectedRowKeys],
  );
  const totalPositions = positions?.length || 0;

  const addNeededFields: PurchaseOrderTableItem[] = pendingPositions.map(
    item => ({
      ...item,
      firstClassAndAbove: achieveFirstClassAndAbove(
        item.bsaMemberId,
        rosterItems,
      ),
      program: getProgramNameFromUnitLong(item.unitLong),
      isUserInSubUnit: isYouthInUserSubunitChecker(
        shouldFilterUsersBySubunits,
        userSubUnitUsersMIDs,
        item.bsaMemberId,
      ),
    }),
  );

  const approvableItems = addNeededFields.filter(item => {
    const approvable =
      item.program === programToProgramTypeName[currentOrg.program];
    if (item.firstClassAndAbove) {
      return approvable || item.program === programTypeName.BOY_SCOUT;
    } else {
      return approvable;
    }
  });
  const notApprovableItems = addNeededFields.filter(item => {
    const notApprovable =
      item.program !== programToProgramTypeName[currentOrg.program];
    if (item.firstClassAndAbove) {
      return notApprovable && item.program !== programTypeName.BOY_SCOUT;
    } else {
      return notApprovable;
    }
  });

  const searchedItems = searchOnItems(searchQuery, approvableItems);
  const searchedItemsNotApprovable = searchOnItems(
    searchQuery,
    notApprovableItems,
  );
  const showNotApprovableTable =
    currentOrg.programType !== programTypeName.CUB_SCOUT &&
    searchedItemsNotApprovable.length > 0;
  const selectedAdvancements = useMemo(() => {
    if (isArrayGroupBySubUnit(searchedItems)) {
      return searchedItems;
    }

    return (searchedItems as PurchaseOrderTableItem[]).filter(
      ({ userPositionId }) =>
        userPositionId && selectedRowKeys.includes(userPositionId),
    );
  }, [searchedItems, selectedRowKeys]);

  const onSelect = useCallback(
    (record: UnitPendingLeadershipPosition) => {
      if (!isInSubUnit(record)) return;
      dispatch(
        setSelectedRowKeys(xor(selectedRowKeys, [record.userPositionId])),
      );
    },
    [dispatch, isInSubUnit, selectedRowKeys],
  );

  const onCheck = useCallback(
    (userPositionId: number[]) => {
      dispatch(setSelectedRowKeys(userPositionId));
    },
    [dispatch],
  );

  const renderTitle = () => (
    <>
      {canApprove && (
        <Button
          id="qa_approveAdvancement"
          disabled={!selectedPositions.length || isOffline}
          className={styles.approveAdvancements}
          color="primary"
          ghost
          noBorder
          uppercase={false}
          fitText
          icon={<MdOutlineOfflinePin />}
          onClick={() => setModalVisible(true)}
        >
          <FormattedMessage
            id="progress.ApproveAdvancementsButton.title"
            values={{ count: selectedPositions.length }}
          />
        </Button>
      )}
    </>
  );

  const rowSelection = {
    selectedRowKeys,
    onChange: onCheck,
    getCheckboxProps: (record: UnitPendingLeadershipPosition) => ({
      disabled: !isInSubUnit(record),
    }),
  };

  const cols = useMemo(
    () => [
      {
        title: <FormattedMessage id="shared.name" />,
        render: (_value: unknown, row: UnitPendingLeadershipPosition) => {
          const personFullName = getPreferredName(row);
          return <span>{personFullName}</span>;
        },
        width: '20rem',
      },
      {
        dataIndex: 'bsaMemberId',
        title: <FormattedMessage id="shared.memberId" />,
        width: '15rem',
      },
      {
        dataIndex: 'rank',
        title: <FormattedMessage id="shared.rank" />,
        width: 160,
      },
      { dataIndex: 'position', title: 'Position' },
      {
        dataIndex: 'startDate',
        width: 130,
        title: <FormattedMessage id="shared.date.startDate" />,
      },
      {
        dataIndex: 'notes',
        title: <FormattedMessage id="shared.notesTitle" />,
        ellipsis: true,
      },
    ],
    [],
  );

  return (
    <>
      <LeadershipApproveModalContainer
        visible={modalVisible}
        selectedPositions={selectedPositions}
        onCancel={() => setModalVisible(false)}
      />
      <div className={styles.tableContainer}>
        {!isMobile && (
          <Table
            className={styles.table}
            title={renderTitle}
            rowKey="userPositionId"
            loading={isLoading || loadingRoster}
            pagination={{
              showTotal: () => {
                const selected = selectedRowKeys?.length;
                return (
                  <PaginationTotal total={totalPositions} selected={selected} />
                );
              },
            }}
            dataSource={searchedItems}
            onRow={(record: UnitPendingLeadershipPosition) => ({
              onClick: () => onSelect(record),
            })}
            rowSelection={rowSelection}
            size="small"
            columns={cols}
            components={{
              body: {
                row: PurchaseOrdersTableCustomRow,
              },
            }}
          />
        )}
        {isMobile && (
          <>
            <TableMobile
              items={searchedItems}
              loading={isLoading}
              rows={rows}
              rowId="userPositionId"
              selectedKeys={selectedRowKeys}
              onItemClick={onSelect}
            />
            <Fab visible={selectedAdvancements.length > 0 && canApprove} inline>
              {renderTitle()}
            </Fab>
          </>
        )}
        {showNotApprovableTable && (
          <div className={styles.notApprovableContainer}>
            <p className={styles.disclaimer}>
              <FormattedMessage id="advancement.PendingItems.nonApprovable.disclaimer" />
            </p>
            {!isMobile && (
              <Table
                className={styles.table}
                columns={cols}
                dataSource={searchedItemsNotApprovable}
                loading={isLoading}
                pagination={false}
              />
            )}
            {isMobile && (
              <TableMobile
                items={searchedItemsNotApprovable}
                loading={isLoading}
                rows={rows}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
};
